import { Component, ElementRef, Input, OnInit, SimpleChanges } from '@angular/core';
import { ChartType, GoogleChart } from './google-charts.model';
import { ChartRow } from './google-charts.model';
import { colors } from './constants';

declare var google: GoogleChart;

@Component({
  selector: 'app-google-charts',
  templateUrl: './google-charts.component.html',
  styleUrls: ['./google-charts.component.scss']
})
export class GoogleChartsComponent implements OnInit {

  static uid = 1;
  uid: string = '';
  maxChartLegend: number = 27

  @Input() type: ChartType = 'LineChart';
  @Input() data: ChartRow[] = [];

  constructor(private elementRef: ElementRef) {
    GoogleChartsComponent.uid += 1;
    this.uid = `${GoogleChartsComponent.uid}`;
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges): void {

    // google.charts.setOnLoadCallback(this.drawChart);
    if (this.data.length > 0) {
      google.charts.load('current', { packages: ['corechart'] });
      this.drawChart();
    }

  }

  typeMapper(type: ChartType): {
    options: any,
    type: 'LineChart',
    builder: any,
    parser: GoogleChartsComponent['parseLineData']
  } | {
    options: any,
    type: 'PieChart',
    builder: any,
    parser: GoogleChartsComponent['parsePieData']
  } | {
    options: any,
    type: 'AreaChart',
    builder: any,
    parser: GoogleChartsComponent['parseAreaData']
  } {
    return {
      LineChart: {
        options: {
          lineWidth: 1,
          backgroundColor: 'transparent',
          legend: {
            position: 'none' as const
          },
          vAxis: {
            minValue: 0,
            maxValue: 5,
            ticks: [0, 1, 2, 3, 4, 5] as number[],
            baselineColor: '#000000'
          },
          pointsVisible: true,
          pointSize: 7,
          chartArea: {
            left: '10%',
            width: '75%',
            height: '75%',
            top: '10%'
          },
          hAxis: {
            showTextEvery: this.data.length - 1,
            minValue: 0,
            maxValue: this.data.length,
          },
          tooltip: {
            isHtml: true,
            // trigger: 'selection'
          },
        },
        type: 'LineChart' as const,
        builder: google.visualization.LineChart,
        parser: this.parseLineData
      },
      PieChart: {
        options: {
          // cores do grafico de pizza é dada no objeto options
          // se alguma linha não tiver cor, usa cor padrão
          colors: this.data
            .sort(([nam1, val1], [nam2, val2]) => Array.isArray(val1) || Array.isArray(val2) ? 0 : val2 - val1)
            .map(([a, b, c], i) =>
              c?.color ? c.color : colors[i]),
          tooltip: {
            isHtml: true,
          },
          legend: {
            alignment: 'start',
            maxLines: 1,
            position: 'right',
          } as const,
          chartArea: {
            left: 0,
            width: '100%',
            height: '90%',
            top: '5%'
          },
          pieSliceTextStyle: { fontSize: 10 },
          pieSliceBorderColor: 'transparent',
          pieSliceText: 'percentage'
        },
        type: 'PieChart' as const,
        builder: google.visualization.PieChart,
        parser: this.parsePieData
      },
      AreaChart: {
        options: {
          legend: {
            position: 'none' as const
          },
          colors: ['#5577ee', '#3366cc'],
          chartArea: {
            left: 70,
            width: 500,
            height: 180
          },
          height: 250,
          hAxis: {
            // showTextEvery : this.data.length - 2,
            format: 'dd/MM/yyyy',
            ticks: this.getAreaChartMinMaxValues()
          }
        },
        type: 'AreaChart' as const,
        builder: google.visualization.AreaChart,
        parser: this.parseAreaData
      }
    }[type]
  }

  getAreaChartMinMaxValues() {
    if (this.data.length == 0) {
      return undefined
    }
    const sortedData = [...this.data].sort((first, second) => (new Date(first[0])).getTime() - (new Date(second[0])).getTime())
    const [minValueDate,] = sortedData[0];
    const [maxValueDate,] = [...sortedData].pop() as ChartRow;
    return [
      new Date(minValueDate),
      new Date(maxValueDate)
    ]

  }

  parseAreaData(data: ChartRow[]) {
    const multiplesValues = Array.isArray(data[0][1]);
    const header = multiplesValues ?
      ['data', 'val1', 'val2'] :
      ['data', 'val']
    return [
      header,
      // ...data,
      ...data
        .sort((first, second) => (new Date(first[0])).getTime() - (new Date(second[0])).getTime())
        .map(([dateString, value]) =>
          [
            new Date(dateString),
            ...(multiplesValues ? (value as number[]) : [value])]),
    ]
  }

  parseLineData(data: ChartRow[]) {
    return [
      //header da tabela
      ['Data', 'valor', { 'type': 'string', 'role': 'style' }, { 'type': 'string', 'role': 'tooltip', 'p': { 'html': true } }],
      ...data.map(([date, value, option], i) =>
        [
          date, //col1 : data em formato string
          value,  // valor numerico
          `point { fill-color: ${option ? option.color : colors[i]};  visible: ${option ? option.visible : true}`, // cor do ponto , dada no array de input ou padrão
          option?.html ??
          `<p style="padding: 5px">
          ${(new Date(date)).toLocaleDateString()} <br/>
          ${value}
        </p>`,
        ]
      )
    ]
  }

  parsePieData = (data: ChartRow[]) => {

    const limitLegendLength = (s: string) => {
      const translated = this.translateCategory(s);
      return translated.length > this.maxChartLegend
        ? `${translated.slice(0, this.maxChartLegend)}...`
        : translated;
    };

    return [
      ['Categoria', 'Quantidade', { type: 'string', role: 'tooltip', p: { html: true } }],
      ...data
        .map(([cat, v, options]) => {

          const translated = this.translateCategory(cat);

          let tooltip = options?.html ?? '';

          if (tooltip) {
            tooltip = tooltip.replace(cat, translated);
          }

          if (!tooltip) {
            tooltip = `
            <div style="padding:8px">
              <b>${translated}</b><br/>
              Eventos: ${v}
            </div>
          `;
          }

          return [
            limitLegendLength(cat),
            v,
            tooltip
          ];
        })
        .sort(([nam1, val1], [nam2, val2]) => (val2 as number) - (val1 as number))
    ];
  };

  private categoryTranslations: Record<string, Record<string, string>> = {
    'pt-BR': {
      "No Toxicity": "Sem toxicidade",
      "Social circumstances": "Circunstâncias sociais",
      "Cardiac disorders": "Distúrbios cardíacos",
      "Skin and subcutaneous tissue disorders": "Distúrbios da pele e dos tecidos subcutâneos",
      "Metabolism and nutrition disorders": "Distúrbios do metabolismo e da nutrição",
      "Ear and labyrinth disorders": "Distúrbios do ouvido e do labirinto",
      "Blood and lymphatic system disorders": "Distúrbios do sangue e do sistema linfático",
      "Immune system disorders": "Distúrbios do sistema imunológico",
      "Nervous system disorders": "Distúrbios do sistema nervoso",
      "Endocrine disorders": "Distúrbios endócrinos",
      "Gastrointestinal disorders": "Distúrbios gastrointestinais",
      "General disorders and administration site conditions": "Distúrbios gerais e condições no local de administração",
      "Hepatobiliary disorders": "Distúrbios hepatobiliares",
      "Musculoskeletal and connective tissue disorders": "Distúrbios musculoesqueléticos e dos tecidos conjuntivos",
      "Eye disorders": "Distúrbios oculares",
      "Renal and urinary disorders": "Distúrbios renais e urinários",
      "Respiratory, thoracic and mediastinal disorders": "Distúrbios respiratórios, torácicos e mediastinais",
      "Vascular disorders": "Distúrbios vasculares",
      "Congenital, familial and genetic disorders": "Doenças congênitas, familiares e genéticas",
      "Pregnancy, puerperium and perinatal conditions": "Gravidez, puerpério e condições perinatais",
      "Infections and infestations": "Infecções e infestações",
      "Investigations": "Investigações",
      "Injury, poisoning and procedural complications": "Lesões, envenenamento e complicações processuais",
      "Benign, malignant and unspecified neoplasms (including cysts and polyps)": "Neoplasias benignas, malignas e não especificadas (incluindo cistos e pólipos)",
      "Surgical and medical procedures": "Procedimentos cirúrgicos e médicos",
      "Reproductive system and breast disorders": "Sistema reprodutivo e distúrbios mamários",
      "Psychiatric disorders": "Transtornos psiquiátricos"
    }
  };

  private translateCategory(value: string): string {
    return this.categoryTranslations['pt-BR']?.[value] ?? value;
  }

  drawChart() {


    google.charts.setOnLoadCallback(() => {

      const { type } = { type: this.type } as const;
      const dataCopy = [...this.data]
      const mapper = this.typeMapper(type);
      const data = google.visualization.arrayToDataTable(
        mapper.parser(dataCopy)
      );

      const domEle = document.getElementById(this.uid);
      if (domEle === null) {
        // todo: exibir msg de erro
        console.error(`elemento ${this.uid} nao encontrado para o gráfico do tipo ${type}`);
        return
      }

      // não consegui fazer o compilador do ts saber que mapper.builder e mapper.options ambos vão ser
      // do tipo correspondente sem ter que usar if-elses irrelevantes
      new mapper.builder(domEle).draw(data, mapper.options as any)
    })
  }

}
